import { RefObject, useEffect } from "react";

// Reference-counted scroll lock, keyed per-element, so multiple pickers
// (or nested ones inside modals/drawers) don't clobber each other's
// overflow restore when closing.
const scrollLockRegistry = new WeakMap<HTMLElement, { count: number; previousOverflow: string }>();

const lockScrollElement = (el: HTMLElement) => {
  const entry = scrollLockRegistry.get(el);
  if (entry) {
    entry.count += 1;
    return;
  }
  scrollLockRegistry.set(el, { count: 1, previousOverflow: el.style.overflow });
  el.style.overflow = "hidden";
};

const unlockScrollElement = (el: HTMLElement) => {
  const entry = scrollLockRegistry.get(el);
  if (!entry) return;
  entry.count -= 1;
  if (entry.count <= 0) {
    el.style.overflow = entry.previousOverflow;
    scrollLockRegistry.delete(el);
  }
};

// Walk up from the anchor's DOM node (works across React portals since it's
// real DOM traversal) and collect every ancestor that actually scrolls —
// custom modals/drawers manage their own overflow:auto container instead of
// relying on document scroll, so locking only <body> misses them. <body> is
// always included as the fallback for window scroll.
const getScrollableAncestors = (el: HTMLElement | null): HTMLElement[] => {
  const result: HTMLElement[] = [];
  let node = el?.parentElement ?? null;
  while (node && node !== document.body) {
    const overflowY = window.getComputedStyle(node).overflowY;
    if ((overflowY === "auto" || overflowY === "scroll") && node.scrollHeight > node.clientHeight) {
      result.push(node);
    }
    node = node.parentElement;
  }
  result.push(document.body);
  return result;
};

// Locks scroll on the outer page (and any scrollable modal/drawer container
// the picker sits in) while its popper is open, so only the popper itself
// can be scrolled. Pass the ref of the picker's wrapper element as the anchor.
export const useScrollLockOnOpen = (open: boolean, anchorRef: RefObject<HTMLElement>) => {
  useEffect(() => {
    if (!open) return;
    const targets = getScrollableAncestors(anchorRef.current);
    targets.forEach(lockScrollElement);
    return () => targets.forEach(unlockScrollElement);
  }, [open]);
};
